import { LayoutDashboard } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function SubscriptionAgreementPage() {
  const effectiveDate = 'January 1, 2026';

  return (
    <div className="min-h-screen" style={{ background: '#F7F6F2' }}>
      <header className="flex items-center justify-between px-8 py-5" style={{ borderBottom: '1px solid #E5E3DB', background: '#FFFFFF' }}>
        <Link to="/" className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#D98A4A' }}>
            <LayoutDashboard size={16} className="text-white" />
          </div>
          <span className="font-heading text-lg font-semibold italic" style={{ color: '#1A1A1A' }}>AI Snugglemind</span>
        </Link>
        <div className="flex items-center gap-4 font-body text-sm">
          <Link to="/terms" className="hover:underline" style={{ color: '#4B5563' }}>Terms of Use</Link>
          <Link to="/privacy" className="hover:underline" style={{ color: '#4B5563' }}>Privacy Policy</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="subscription-agreement-page">
        <h1 className="font-heading text-4xl font-medium mb-2" style={{ color: '#1A1A1A' }}>Subscription Agreement</h1>
        <p className="font-body text-sm mb-10" style={{ color: '#9CA3AF' }}>Effective Date: {effectiveDate}</p>

        <div className="space-y-8 font-body text-sm leading-relaxed" style={{ color: '#4B5563' }}>
          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>1. Subscription Plans</h2>
            <p>Snugglemind Inc. ("Company") offers the following subscription plans for the AI Snugglemind platform ("Service"):</p>
            <div className="mt-3 rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
              <table className="w-full">
                <thead>
                  <tr style={{ background: 'rgba(198,156,109,0.08)' }}>
                    <th className="text-left px-4 py-3 font-semibold" style={{ color: '#1A1A1A', borderBottom: '1px solid #E5E3DB' }}>Plan</th>
                    <th className="text-left px-4 py-3 font-semibold" style={{ color: '#1A1A1A', borderBottom: '1px solid #E5E3DB' }}>Price</th>
                    <th className="text-left px-4 py-3 font-semibold" style={{ color: '#1A1A1A', borderBottom: '1px solid #E5E3DB' }}>Billing</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-3" style={{ borderBottom: '1px solid #E5E3DB' }}>Monthly</td>
                    <td className="px-4 py-3 font-mono" style={{ borderBottom: '1px solid #E5E3DB' }}>$49 per user/month</td>
                    <td className="px-4 py-3" style={{ borderBottom: '1px solid #E5E3DB' }}>Billed monthly</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Annual</td>
                    <td className="px-4 py-3 font-mono">$29 per user/month</td>
                    <td className="px-4 py-3">Billed annually ($348/year)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>2. Free Trial</h2>
            <p>New users are eligible for a seven (7) day free trial of the Service. During the trial period:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>No credit card or payment information is required to start the trial</li>
              <li>You will have full access to all features of the Service</li>
              <li>The trial begins on the date of account creation</li>
              <li>At the end of the trial period, access will be limited to a read-only dashboard view until a paid subscription is activated</li>
              <li>Each individual or organization is eligible for one (1) free trial only</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>3. Billing and Payment</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>All payments are processed securely through Stripe. Snugglemind Inc. does not store your credit card details.</li>
              <li>For monthly plans, your subscription will automatically renew each month on the anniversary of your initial payment date.</li>
              <li>For annual plans, your subscription will automatically renew each year on the anniversary of your initial payment date.</li>
              <li>All prices are listed in United States Dollars (USD) and are exclusive of applicable taxes.</li>
              <li>You are responsible for any applicable taxes based on your jurisdiction.</li>
              <li>Price changes will be communicated at least thirty (30) days in advance and will apply at the next billing cycle.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>4. Cancellation and Refunds</h2>
            <p>You may cancel your subscription at any time. Upon cancellation:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Your subscription will remain active until the end of the current billing period</li>
              <li>You will not be charged for subsequent billing periods after cancellation</li>
              <li>Monthly subscriptions: No refunds are provided for partial months</li>
              <li>Annual subscriptions: Refunds may be issued on a pro-rata basis within the first thirty (30) days of a new billing cycle at the Company's discretion</li>
              <li>After cancellation takes effect, your access will revert to the limited read-only dashboard view</li>
              <li>Your data will be retained for ninety (90) days after cancellation, after which it may be permanently deleted</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>5. Service Level</h2>
            <p>We strive to maintain high availability of the Service. However:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>The Service is provided "as is" without warranty of uninterrupted or error-free operation</li>
              <li>Scheduled maintenance will be communicated in advance when possible</li>
              <li>We are not liable for any downtime, data loss, or damages resulting from service interruptions</li>
              <li>AI-powered features depend on third-party AI providers and may be temporarily unavailable</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>6. Per-User Pricing</h2>
            <p>Subscription pricing is on a per-user basis. Each user who accesses the Service must be covered by a paid seat. You agree to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Not share login credentials between multiple individuals</li>
              <li>Add additional user seats as needed when inviting team members</li>
              <li>Pay the applicable per-user rate for each active user on your account</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>7. Data Ownership</h2>
            <p>You retain full ownership of all financial data, transaction records, client profiles, and other content you upload to the Service. Snugglemind Inc. does not claim any ownership rights over your data. Upon termination, you may export your data before the retention period expires.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>8. Modification of Terms</h2>
            <p>Snugglemind Inc. reserves the right to modify this Subscription Agreement at any time. Material changes will be communicated via email or in-app notification at least thirty (30) days before they take effect. Continued use of the Service after modifications constitutes acceptance of the updated terms.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>9. Governing Law</h2>
            <p>This Subscription Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-medium mb-3" style={{ color: '#1A1A1A' }}>10. Contact Us</h2>
            <p>For billing inquiries, cancellation requests, or questions about this Subscription Agreement, please contact us at:</p>
            <p className="mt-2"><strong style={{ color: '#1A1A1A' }}>Snugglemind Inc.</strong><br />Email: admin@snugglemind.com</p>
          </section>
        </div>

        <div className="mt-12 pt-6 flex items-center gap-4 font-body text-xs" style={{ borderTop: '1px solid #E5E3DB', color: '#9CA3AF' }}>
          <span>&copy; {new Date().getFullYear()} Snugglemind Inc. All rights reserved.</span>
          <Link to="/terms" className="hover:underline" style={{ color: '#D98A4A' }}>Terms of Use</Link>
          <Link to="/privacy" className="hover:underline" style={{ color: '#D98A4A' }}>Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}
